import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SharedService } from '@shared/service/sharedservice';
import { PackageLimitService } from '@shared/service/package-limit.service';
import { forkJoin, of } from 'rxjs';
import { take, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnDestroy {
  activeMenuItem: string = 'Dashboard';
  menuItems: any[] = [];
  menuItemsCopy: any[] = [];
  submenuTimeout: any;
  userAccess: any;
  isSuperAdmin: boolean = false;

  private roleBasePath: string = 'admin';
  private roleSubscription?: Subscription;
  private currentRole: string = 'Admin';
  private staffCheckInSubscription?: Subscription;
  private isStaffCheckedIn = false;
  private isPremiumPackage: boolean = true; // Default to true to show menu until we check

  constructor(
    private router: Router,
    private shareService: SharedService,
    private packageLimitService: PackageLimitService
  ) {
    this.userAccess = this.shareService.getUserAccess();
    this.isSuperAdmin = this.loadSuperAdminFlag();
    this.currentRole = this.shareService.getActiveRole();
    this.roleBasePath = this.shareService.getActiveRoleBasePath();
    this.isStaffCheckedIn = this.shareService.getStaffCheckedIn();
    this.checkPackageAndBuildMenu();
    this.roleSubscription = this.shareService.getCurrentRole().subscribe(role => {
      this.currentRole = role ?? 'Admin';
      this.roleBasePath = this.shareService.getBasePathForRole(role);
      this.checkPackageAndBuildMenu();
    });
    this.staffCheckInSubscription = this.shareService.staffCheckedIn$.subscribe(status => {
      this.isStaffCheckedIn = status;
      this.checkPackageAndBuildMenu();
    });
  }

  private checkPackageAndBuildMenu(): void {
    // Get schoolId from claim
    const claimRaw = localStorage.getItem('userClaim');
    let schoolId: number | null = null;
    if (claimRaw) {
      try {
        const parsed = JSON.parse(claimRaw);
        schoolId = Number(parsed.SchoolId) || Number(parsed.schoolId) || null;
      } catch {
        schoolId = null;
      }
    }

    if (schoolId) {
      this.packageLimitService.getSchoolConfiguration(schoolId).pipe(
        take(1),
        catchError(() => of(null))
      ).subscribe((config) => {
        if (config) {
          const packageName = (config.package || '').toLowerCase();
          // Show registration menu for Premium and Standard packages
          this.isPremiumPackage = packageName === 'premium' || packageName === 'standard';
        } else {
          // If no config, default to premium (show menu)
          this.isPremiumPackage = true;
        }
        this.buildMenu();
      });
    } else {
      // No schoolId, default to premium (show menu)
      this.isPremiumPackage = true;
      this.buildMenu();
    }
  }

  ngOnDestroy(): void {
    this.roleSubscription?.unsubscribe();
    this.staffCheckInSubscription?.unsubscribe();
  }

  setActiveMenuItem(item: string, subMenu: any) {
    this.activeMenuItem = item;
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });

    if (subMenu.isExternal) {
      window.location.href = subMenu.link;
      return;
    }

    const navigationExtras = subMenu.queryParams ? { queryParams: subMenu.queryParams } : undefined;
    if (navigationExtras) {
      this.router.navigate([subMenu.link], navigationExtras);
    } else {
      this.router.navigate([subMenu.link]);
    }
  }

  showSubmenu(item: any) {
    clearTimeout(this.submenuTimeout);
    item.showSubmenu = true;
  }

  hideSubmenu(item: any) {
    this.submenuTimeout = setTimeout(() => {
      item.showSubmenu = false;
    }, 200);
  }

  stayOpen(item: any) {
    clearTimeout(this.submenuTimeout);
    item.showSubmenu = true;
  }

  onRightClick(_: MouseEvent, __: any): void {
    // Placeholder for future context-menu logic.
  }

  private loadSuperAdminFlag(): boolean {
    const userClaimRaw = localStorage.getItem('userClaim');
    if (!userClaimRaw) {
      return false;
    }

    try {
      const parsedClaim = JSON.parse(userClaimRaw);
      const flag = (parsedClaim?.IsSuperAdmin ?? parsedClaim?.isSuperAdmin ?? '').toString().toLowerCase();
      return flag === 'true';
    } catch {
      return false;
    }
  }

  private buildUrl(relative: string = ''): string {
    const cleaned = relative.startsWith('/') ? relative.substring(1) : relative;
    return `/${this.roleBasePath}/${cleaned}`.replace(/\/+/g, '/');
  }

  private hasAdminAccess(module: string): boolean {
    return !!this.userAccess?.find(
      (item: any) => item.Module === module && item.AccessLevel === 'Admin'
    );
  }

  private buildMenu(): void {
    const roleUrl = (path: string) => this.buildUrl(path);
    const normalizedRole = (this.currentRole ?? '').toLowerCase();
    const isStaffView = normalizedRole === 'staff';
    const isParentView = normalizedRole === 'parent';
    const isAdminRole = normalizedRole === 'admin';
    if (isParentView) {
      this.menuItems = [];
      this.menuItemsCopy = [];
      return;
    }

    const studentBaseUrl = roleUrl('student/listing');
    const studentSubmenu = isStaffView
      ? [
          ...(this.isStaffCheckedIn
            ? [{ name: 'Check-In / Check-Out Assignments', link: studentBaseUrl, queryParams: { tab: 'checkin' }, isExternal: false }]
            : []),
          { name: 'Master Staff Assignment', link: studentBaseUrl, queryParams: { tab: 'master-staff-assignee' }, isExternal: false },
          { name: 'Temporary Staff Assignment', link: studentBaseUrl, queryParams: { tab: 'temporary-staff-assignee' }, isExternal: false }
        ]
      : [
          { name: 'Student List', link: studentBaseUrl, queryParams: { tab: 'student' }, isExternal: false },
          { name: 'Create New Student', link: roleUrl('student/create'), isExternal: false },
          { name: 'Attendance', link: studentBaseUrl, queryParams: { tab: 'attendance' }, isExternal: false },
          { name: 'Check-In / Check-Out Assignments', link: studentBaseUrl, queryParams: { tab: 'checkin' }, isExternal: false },
          { name: 'Master Staff Assignment', link: studentBaseUrl, queryParams: { tab: 'master-staff-assignee' }, isExternal: false },
          { name: 'Temporary Staff Assignment', link: studentBaseUrl, queryParams: { tab: 'temporary-staff-assignee' }, isExternal: false }
        ];
    const studentNavHasAccess = this.hasAdminAccess('Student') || isStaffView;
    const studentUrl = studentBaseUrl;

    this.menuItems = [
      {
        name: 'Dashboard',
        id: 'Dashboard',
        url: roleUrl('dashboard'),
        icon: 'assets/images/dashboard.svg',
        hasAccess: true,
        statussub: true,
        lastmenu: false,
        isExternal: false,
        showSubmenu: false,
        submenuitem: [
          { name: 'My Dashboard', link: roleUrl('dashboard'), isExternal: false }
        ]
      },
      {
        name: 'Students',
        id: 'Student',
        url: studentUrl,
        icon: 'assets/images/parent.svg',
        hasAccess: studentNavHasAccess,
        statussub: true,
        lastmenu: false,
        isExternal: false,
        showSubmenu: false,
        submenuitem: studentSubmenu
      },
      {
        name: 'Users',
        id: 'User',
        url: roleUrl('user/listing'),
        icon: 'assets/images/staff.svg',
        hasAccess: !isStaffView && this.hasAdminAccess('User'),
        statussub: true,
        lastmenu: false,
        isExternal: false,
        showSubmenu: false,
        submenuitem: [
          { name: 'User List', link: roleUrl('user/listing'), queryParams: { tab: 'user' }, isExternal: false },
          { name: 'Create New User', link: roleUrl('user/create'), isExternal: false },
          { name: 'Staff Attendance', link: roleUrl('user/listing'), queryParams: { tab: 'attendance' }, isExternal: false },
          // { name: 'Staff Assignments', link: roleUrl('staffCheckIn'), queryParams: { tab: 'assignments' }, isExternal: false },
          // { name: 'Master Staff Assignment', link: roleUrl('staffCheckIn'), queryParams: { tab: 'master' }, isExternal: false },
          // { name: 'Temporary Staff Assignment', link: roleUrl('staffCheckIn'), queryParams: { tab: 'temporary' }, isExternal: false },
          // { name: 'Overall Assignment Overview', link: roleUrl('staffCheckIn'), queryParams: { tab: 'overview' }, isExternal: false }
        ]
      },
      {
        name: 'Classes',
        id: 'Class',
        url: roleUrl('class/listing'),
        icon: 'assets/images/student.svg',
        hasAccess: this.hasAdminAccess('Class'),
        statussub: true,
        lastmenu: false,
        isExternal: false,
        showSubmenu: false,
        submenuitem: [
          { name: 'Class List', link: roleUrl('class/listing'), queryParams: { tab: 'class' }, isExternal: false },
          { name: 'Create New Class', link: roleUrl('class/create'), isExternal: false },
          { name: 'Grade List', link: roleUrl('class/listing'), queryParams: { tab: 'grade' }, isExternal: false },
          { name: 'Create New Grade', link: roleUrl('class/listing'), queryParams: { tab: 'grade', action: 'create-grade' }, isExternal: false },
          { name: 'Operating Hours', link: roleUrl('class/listing'), queryParams: { tab: 'operating-hour' }, isExternal: false },
          { name: 'Create Operating Hours', link: roleUrl('class/listing'), queryParams: { tab: 'operating-hour', action: 'create-operating-hour' }, isExternal: false }
        ]
      },
      {
        name: 'Finance',
        id: 'Payment',
        url: roleUrl('fee/listing'),
        icon: 'assets/images/payment.svg',
        hasAccess: this.hasAdminAccess('Payment'),
        statussub: true,
        lastmenu: false,
        isExternal: false,
        showSubmenu: false,
        submenuitem: [
          { name: 'Fee Management', link: roleUrl('fee/listing'), isExternal: false },
          { name: 'Payments', link: roleUrl('payment/listing'), isExternal: false }
        ]
      },
      {
        name: 'Registration',
        id: 'Online Registration',
        url: roleUrl('registration/listing'),
        icon: 'assets/images/register.svg',
        hasAccess: this.hasAdminAccess('Online Registration') && this.isPremiumPackage,
        statussub: true,
        lastmenu: false,
        isExternal: false,
        showSubmenu: false,
        submenuitem: [
          { name: 'Application Approvals', link: roleUrl('registration/listing'), queryParams: { tab: 'student' }, isExternal: false },
          { name: 'Enrolled Students', link: roleUrl('registration/listing'), queryParams: { tab: 'grade' }, isExternal: false }
        ]
      },
      {
        name: 'Calendar',
        id: 'Calendar',
        url: roleUrl('calendar'),
        icon: 'assets/images/calendar.svg',
        hasAccess: !isStaffView,
        statussub: true,
        lastmenu: false,
        isExternal: false,
        showSubmenu: false,
        submenuitem: [
          { name: 'View Academic', link: roleUrl('calendar'), isExternal: false }
        ]
      },
      {
        name: 'Setting',
        id: 'Admin',
        url: roleUrl('school/listing'),
        icon: 'assets/images/utilities.svg',
        hasAccess: this.isSuperAdmin || isAdminRole || this.hasAdminAccess('Online Registration'),
        statussub: true,
        lastmenu: false,
        isExternal: false,
        showSubmenu: false,
        submenuitem: [
          ...(this.isSuperAdmin || isAdminRole
            ? [{ name: 'School Information', link: roleUrl('school/listing'), queryParams: { tab: 'school' }, isExternal: false }]
            : []),
          ...(this.isSuperAdmin
            ? [
                { name: 'Payment History', link: roleUrl('school/listing'), queryParams: { tab: 'payment' }, isExternal: false },
                { name: 'System Configuration', link: roleUrl('school/listing'), queryParams: { tab: 'ref-config' }, isExternal: false }
              ]
            : []),
          { name: 'Whatsapp Template', link: '/template/whatsapp/listing', isExternal: false }
        ]
      }
    ];

    if (isStaffView) {
      this.menuItems = this.menuItems.filter(item => item.id === 'Dashboard' || item.id === 'Student');
    }

    this.menuItemsCopy = [...this.menuItems];
    this.shareService.sendMenuItems(this.menuItems);
  }
}
